import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';
import { LegalProfileForm, CounterpartySubResourceSection, CounterpartyCoreInfoEditor } from './CounterpartyEditSections';

interface CounterpartyDetail {
  id: string;
  name: string;
  shortName: string;
  edrpou: string;
  taxId?: string;
  country: string;
  status: string;
  onboardingStatus: string;
  createdAt: string;
  legalProfile?: {
    fullLegalName: string;
    legalAddress: string;
    actualAddress?: string;
    registrationCountry: string;
    vatStatus: string;
    vatNumber?: string;
    directorName: string;
    authorizedPerson?: string;
    basisOfAuthority?: string;
  };
  contacts?: Array<{
    id: string;
    fullName: string;
    position: string;
    email: string;
    phone: string;
    contactType: string;
    isPrimary: boolean;
  }>;
  bankAccounts?: Array<{
    id: string;
    bankName: string;
    iban: string;
    currency: string;
    mfo: string;
    accountHolder: string;
    isPrimary: boolean;
  }>;
  assets?: Array<{
    id: string;
    assetName: string;
    assetType: string;
    installedCapacityMw: number;
    operationalStatus: string;
  }>;
  contracts?: Array<{
    id: string;
    contractType: string;
    contractNumber: string;
    startDate: string;
    endDate: string;
    status: string;
  }>;
  documents?: Array<{
    id: string;
    documentType: string;
    fileName: string;
    status: string;
    uploadedAt: string;
  }>;
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'legal', label: 'Legal Profile' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'bank', label: 'Bank Accounts' },
  { key: 'assets', label: 'Assets' },
  { key: 'contracts', label: 'Contracts' },
  { key: 'documents', label: 'Documents' },
  { key: 'financial', label: 'Financial Terms' },
];

export default function CounterpartyDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [data, setData] = useState<CounterpartyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#8E8E93' : '#6B6B70';
  const sectionBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const tabBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const tableHeaderBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const tableBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const iconBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const docBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const onboardingBg = isDark ? 'rgba(31,91,143,0.12)' : 'rgba(31,91,143,0.1)';

  useEffect(() => {
    if (id) loadCounterparty();
  }, [id]);

  const loadCounterparty = async () => {
    setLoading(true);
    try {
      const result = await api.get<CounterpartyDetail>(`/counterparties/${id}`);
      setData(result);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) { setData(null); }
      else { const message = getApiErrorMessage(err, t('cp_err_load')); addToast(message, 'error'); }
    } finally { setLoading(false); }
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, { bg: string; color: string }> = {
      active: { bg: 'rgba(13,122,60,0.1)', color: '#0D7A3C' },
      pending: { bg: 'rgba(184,134,11,0.1)', color: '#B8860B' },
      suspended: { bg: 'color-mix(in srgb, var(--warn) 12%, transparent)', color: 'var(--warn)' },
      draft: { bg: 'rgba(107,107,112,0.1)', color: '#6B6B70' },
    };
    const style = map[status] ?? map.draft;
    return <span className="text-xs font-medium px-2.5 py-1 rounded-full capitalize" style={{ background: style.bg, color: style.color }}>{status}</span>;
  };

  if (loading) {
    return (
      <DashboardLayout breadcrumb={[t('cp_section'), 'Loading...']}>
        <div className="p-12 text-center">
          <i className="ri-loader-4-line animate-spin text-2xl" style={{ color: textMuted }}></i>
          <p className="text-sm mt-3" style={{ color: textMuted }}>Loading counterparty...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout breadcrumb={[t('cp_section'), t('common_not_found')]}>
        <div className="p-12 text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: iconBg }}>
            <i className="ri-error-warning-line text-xl" style={{ color: 'var(--accent)' }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: textMain }}>Counterparty not found</p>
          <button onClick={() => navigate('/counterparties')} className="mt-4 px-4 py-2 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap" style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}>
            Back to List
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout breadcrumb={[t('cp_section'), data.name]}>
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => navigate('/counterparties')} className="w-8 h-8 flex items-center justify-center rounded-md cursor-pointer transition-colors" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'transparent' }}>
            <i className="ri-arrow-left-line" style={{ color: textMuted }}></i>
          </button>
          <h1 className="text-2xl font-bold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
            {data.name}
          </h1>
          {getStatusBadge(data.status)}
        </div>
        <div className="flex items-center gap-4 text-sm ml-11" style={{ color: textMuted }}>
          <span>EDRPOU: <strong className="font-mono" style={{ color: textMain }}>{data.edrpou}</strong></span>
          <span>·</span>
          <span>{data.country}</span>
          <span>·</span>
          <span>Created {new Date(data.createdAt).toLocaleDateString('uk-UA')}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0 mb-6 overflow-x-auto" style={{ borderBottom: `1px solid ${tabBorder}` }}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="px-4 py-3 text-sm font-medium cursor-pointer transition-colors whitespace-nowrap relative"
            style={{
              color: activeTab === tab.key ? 'var(--accent)' : textMuted,
              borderBottom: activeTab === tab.key ? '2px solid var(--accent)' : '2px solid transparent',
              marginBottom: '-1px',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="rounded-xl p-6" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
              <h3 className="text-sm font-semibold mb-4" style={{ color: textMain }}>Core information</h3>
              <CounterpartyCoreInfoEditor counterpartyId={data.id} initial={{ name: data.name, shortName: data.shortName, edrpou: data.edrpou, taxId: data.taxId, country: data.country, status: data.status }} onSaved={loadCounterparty} />
            </div>
            <div className="original-overview">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-4 rounded-lg" style={{ background: sectionBg }}>
                  <div className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: textMuted }}>Company</div>
                  <div className="space-y-2">
                    {[
                      [t('cp_full_name'), data.name],
                      ['Short Name', data.shortName],
                      ['EDRPOU', data.edrpou],
                      [t('cp_country'), data.country],
                      ...(data.taxId ? [['Tax ID', data.taxId]] as const : []),
                    ].map(([label, val]) => (
                      <div className="flex justify-between text-sm" key={label}>
                        <span style={{ color: textMuted }}>{label}</span>
                        <span className={`font-medium ${label === 'EDRPOU' ? 'font-mono' : ''}`} style={{ color: textMain }}>{val}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-4 rounded-lg" style={{ background: sectionBg }}>
                  <div className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: textMuted }}>Status</div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm items-center">
                      <span style={{ color: textMuted }}>Status</span>
                      {getStatusBadge(data.status)}
                    </div>
                    <div className="flex justify-between text-sm items-center">
                      <span style={{ color: textMuted }}>Onboarding</span>
                      <span className="text-xs font-medium px-2 py-1 rounded-full capitalize" style={{ background: onboardingBg, color: '#1F5B8F' }}>
                        {data.onboardingStatus.replace('_', ' ')}
                      </span>
                    </div>
                    {[['Assets', data.assets?.length ?? 0], ['Contracts', data.contracts?.length ?? 0], ['Documents', data.documents?.length ?? 0]].map(([l, v]) => (
                      <div className="flex justify-between text-sm" key={l}>
                        <span style={{ color: textMuted }}>{l}</span>
                        <span className="font-medium" style={{ color: textMain }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-4 rounded-lg" style={{ background: sectionBg }}>
                  <div className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: textMuted }}>Quick Actions</div>
                  <div className="space-y-2">
                    {[
                      { label: t('cp_add_asset'), icon: 'ri-add-line', action: () => navigate(`/counterparties/${data.id}/assets/new`) },
                      { label: t('cp_add_contract'), icon: 'ri-file-list-3-line', action: () => setActiveTab('contracts') },
                      { label: 'Upload Document', icon: 'ri-folder-line', action: () => setActiveTab('documents') },
                    ].map((btn) => (
                      <button
                        key={btn.label}
                        onClick={btn.action}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer transition-colors"
                        style={{ color: textMain }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : '#fff'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <i className={btn.icon} style={{ color: 'var(--accent)' }}></i> {btn.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'legal' && <LegalProfileForm counterpartyId={data.id} initial={data.legalProfile as any} onSaved={loadCounterparty} />}
        {activeTab === 'contacts' && <CounterpartySubResourceSection type="contact" counterpartyId={data.id} items={(data.contacts || []) as any} onChange={loadCounterparty} />}
        {activeTab === 'bank' && <CounterpartySubResourceSection type="bank" counterpartyId={data.id} items={(data.bankAccounts || []) as any} onChange={loadCounterparty} />}

        {activeTab === 'assets' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold" style={{ color: textMain }}>Assets ({data.assets?.length ?? 0})</h3>
              <button onClick={() => navigate(`/counterparties/${data.id}/assets/new`)} className="px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap" style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', color: 'var(--text)' }}>
                <i className="ri-add-line mr-1"></i> Add Asset
              </button>
            </div>
            {data.assets && data.assets.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: tableHeaderBg }}>
                      {['Name', 'Type', 'Capacity', 'Status', ''].map((h) => (
                        <th key={h} className={`text-left px-4 py-2 font-semibold text-xs uppercase`} style={{ color: textMuted }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.assets.map((asset) => (
                      <tr key={asset.id} className="cursor-pointer transition-colors" onClick={() => navigate(`/assets/${asset.id}`)} style={{ borderBottom: `1px solid ${tableBorder}` }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : '#F9F9FB'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <td className="px-4 py-3 font-medium" style={{ color: textMain }}>{asset.assetName}</td>
                        <td className="px-4 py-3 capitalize" style={{ color: textMuted }}>{asset.assetType.replace('_', ' ')}</td>
                        <td className="px-4 py-3" style={{ color: textMain }}>{asset.installedCapacityMw} МВт</td>
                        <td className="px-4 py-3">
                          <span className="text-xs font-medium px-2 py-1 rounded-full capitalize" style={{ background: sectionBg, color: textMain }}>
                            {asset.operationalStatus.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3"><i className="ri-arrow-right-s-line" style={{ color: textMuted }}></i></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm" style={{ color: textMuted }}>No assets registered yet</p>
                <button onClick={() => navigate(`/counterparties/${data.id}/assets/new`)} className="mt-3 px-4 py-2 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap" style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', color: 'var(--text)' }}>
                  Add First Asset
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'contracts' && (
          <div>
            {data.contracts && data.contracts.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: tableHeaderBg }}>
                      {['Number', 'Type', 'Period', 'Status'].map((h) => (
                        <th key={h} className="text-left px-4 py-2 font-semibold text-xs uppercase" style={{ color: textMuted }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.contracts.map((contract) => (
                      <tr key={contract.id} style={{ borderBottom: `1px solid ${tableBorder}` }}>
                        <td className="px-4 py-3 font-medium" style={{ color: textMain }}>{contract.contractNumber}</td>
                        <td className="px-4 py-3 capitalize" style={{ color: textMuted }}>{contract.contractType.replace('_', ' ')}</td>
                        <td className="px-4 py-3 text-xs" style={{ color: textMuted }}>
                          {new Date(contract.startDate).toLocaleDateString('uk-UA')} — {new Date(contract.endDate).toLocaleDateString('uk-UA')}
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(contract.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8"><p className="text-sm" style={{ color: textMuted }}>No contracts available</p></div>
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div>
            {data.documents && data.documents.length > 0 ? (
              <div className="space-y-2">
                {data.documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg" style={{ border: `1px solid ${docBorder}` }}>
                    <div className="flex items-center gap-3">
                      <i className="ri-file-text-line text-lg" style={{ color: 'var(--accent)' }}></i>
                      <div>
                        <div className="text-sm font-medium" style={{ color: textMain }}>{doc.fileName}</div>
                        <div className="text-xs" style={{ color: textMuted }}>{doc.documentType} · {new Date(doc.uploadedAt).toLocaleDateString('uk-UA')}</div>
                      </div>
                    </div>
                    {getStatusBadge(doc.status)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8"><p className="text-sm" style={{ color: textMuted }}>No documents uploaded</p></div>
            )}
          </div>
        )}

        {activeTab === 'financial' && (
          <div className="text-center py-8">
            <p className="text-sm" style={{ color: textMuted }}>Financial terms will be available after asset configuration</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}